import { useMemo } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { t, jt } from 'ttag'

import { SG, SG80T, SLB, SLB80T, SO, SO80T, PB40T } from '@h2/components/colors'
import { features } from '@h2/components/media'
import { Checklist } from '@h2/components/containers/checklist'
import { Heading } from '@h2/components/typography/heading'
import { Icon } from '@h2/components/display/icon'
import educatorSvg from '@h2/assets/svg/home/source/educator.svg'

import { backgroundCurve } from './mixins'

const lists = {
  research: [
    t`Assign activities in addition to custom tasks`,
    t`Track their progress`,
    t`Report on outcomes`
  ],
  goal: [
    t`Suggest items for users to review`,
    t`Make comments on their activities`,
    t`Gain insight into their desired path`
  ],
  action: [
    t`See when someone is falling behind`,
    t`Help users complete their tasks`,
    t`Visualize your users journey`
  ]
}
const wrkingWithOthers = <span key="wrk">{t`working with others`}</span>
const colors = {
  blue: SLB,
  green: SG,
  orange: SO
}
const lightColors = {
  blue: SLB80T,
  green: SG80T,
  orange: SO80T
}
const Header = styled.header`
  width: 60%;

  h3 {
    font-size: 30px;
    line-height: 34px;
  }

  span[role='img'] {
    position: absolute;
    top: 4px;
    right: 0;
  }

  @media (${features.minWidth.desktop}) {
    width: 80%;

    h3 {
      font-size: 26px;
    }

    span[role='img'] {
      top: 25px;

      svg {
        width: 125px;
      }
    }
  }
`
const Article = styled.article`
  position: relative;
  width: 100%;
  padding: 15px 5px 20px 5px;
  box-sizing: border-box;

  &::before {
    display: block;
    position: absolute;
    content: '';
    width: 100%;
    height: 3px;
    left: 0;
    top: 94px;
    background-image: linear-gradient(
      to left,
      transparent 0 180px,
      black 180px 300px,
      transparent 300px 100%
    );
  }

  @media (${features.minWidth.desktop}) {
    ${backgroundCurve};
    box-sizing: border-box;
    width: 279px;
    padding: 20px;
    border-radius: 6px;

    &::before {
      top: 93px;
      height: 2px;
      background-image: linear-gradient(
        to left,
        transparent 0 135px,
        black 135px 265px,
        transparent 265px 100%
      );
    }

    > h4 {
      margin-top: 20px;
    }
  }
`
const WorkingWithOthers = ({ shade, type, size = 'medium' }) => {
  const iconSrc = useMemo(() => {
    return educatorSvg.replace(/#abc123|#bcd234/g, match => {
      if (/#a/.test(match)) {
        return colors[shade]
      }

      return lightColors[shade]
    })
  }, [shade])

  return (
    <Article shade={shade}>
      <Header>
        <Heading level="3" weight="light" variant="mixed-weight" removeMargins>
          {jt`When ${wrkingWithOthers}`}
        </Heading>
        <Icon color={SG} width="165px" height="auto" dangerouslySetGlyph={iconSrc} />
      </Header>
      <Heading level="4" weight="light" mt="24px" mb="15px" color={colors[shade]}>
        {t`An account allows you to:`}
      </Heading>
      <Checklist size={size} color={PB40T} variant="smaller-text">
        {lists[type].map((item, idx) => (
          <Checklist.Item key={idx}>{item}</Checklist.Item>
        ))}
      </Checklist>
    </Article>
  )
}

WorkingWithOthers.propTypes = {
  size: PropTypes.oneOf(['small', 'medium']),
  shade: PropTypes.oneOf(['green', 'orange', 'blue']).isRequired,
  type: PropTypes.oneOf(['research', 'goal', 'action']).isRequired
}

export { WorkingWithOthers }
